import React, { useState } from "react";
import { InteractionModalContainer } from ".";
import { Row, Col } from "react-bootstrap";
import usePoolToken from "../../hooks/usePoolToken";
import RiskMetrics from "../RiskMetrics";
import InputAmount from "../InputAmount";
import InteractionButton from "../InteractionButton";
import TransactionSize from "./TransactionRecap/TransactionSize";
import SupplyAPR from "./TransactionRecap/SupplyAPR";
import useApprove from "../../hooks/useApprove";
import useDeposit from "../../hooks/useDeposit";
import { useToBigNumber } from "../../hooks/useConverter";
import { useAddLiquidityUrl } from "../../hooks/useUrlGenerator";
import { useOnchainAccountPoolToken } from "../../hooks/useOnchain";
import useGenericCall from "../../hooks/useGenericCall";
import { ApprovalType, PoolTokenType, hrAmms, Amms, OffchainCollateral, OffchainBorrowable } from "impermax-sdk";

/**
 * Props for the deposit interaction modal.
 * @property show Shows or hides the modal.
 * @property toggleShow A function to update the show variable to show or hide the Modal.
 */
export interface DepositInteractionModalProps {
  show: boolean;
  toggleShow(s: boolean): void;
}

export default function DepositInteractionModal({show, toggleShow}: DepositInteractionModalProps) {
  const poolTokenType = usePoolToken();
  const [val, setVal] = useState<number>(0);

  const data = useGenericCall(useOnchainAccountPoolToken(), async onchainAccountPoolToken => {
    const availableBalance = await onchainAccountPoolToken.getAvailableBalance();
    const offchainPoolToken = await onchainAccountPoolToken.getPoolToken().getOffchainPoolToken();
    const offchainLendingPol = poolTokenType == PoolTokenType.Collateral ?
      (offchainPoolToken as OffchainCollateral).getLendingPool() :
      (offchainPoolToken as OffchainBorrowable).getLendingPool();
    return {
      symbol: await offchainPoolToken.getSymbol(),
      availableBalance,
      amm: await offchainLendingPol.getAmm(),
      invalidInput: val > availableBalance,
    }
  }, [val]);

  const addLiquidityUrl = useAddLiquidityUrl();
  const amount = useToBigNumber(val);

  const [approvalState, onApprove, permitData] = useApprove(ApprovalType.UNDERLYING, amount, data && data.invalidInput);
  const [depositState, deposit] = useDeposit(approvalState, amount, data && data.invalidInput, permitData);
  const onDeposit = async () => {
    await deposit();
    setVal(0);
    toggleShow(false);
  }

  if (!data) return null;

  if (!data.availableBalance) return (
    <InteractionModalContainer title={poolTokenType == PoolTokenType.Collateral ? "Deposit" : "Supply"} show={show} toggleShow={toggleShow}><>
      You need to hold {data.symbol} { poolTokenType == PoolTokenType.Collateral && 'LP ' }in your wallet in order to deposit it.
      { poolTokenType == PoolTokenType.Collateral ? (<>
        <br/>You can obtain it by <a target="_blank" href={addLiquidityUrl}>providing liquidity</a> to {hrAmms[data.amm]}.
        {data.amm === Amms.solidly && (<>
          <br/>NOTICE: in order to obtain the LP instead of selecting "Deposit & Stake" on Solidly select "Deposit LP".
        </>)}
        {data.amm === Amms.fenix && (<>
          <br/>If you already have provided liquidity but it's not appearing on our UI, unstake the LP from Fenix.
        </>)}
      </>) : null }
    </></InteractionModalContainer>
  );

  return (
    <InteractionModalContainer title={poolTokenType == PoolTokenType.Collateral ? "Deposit" : "Supply"} show={show} toggleShow={toggleShow}><>
      { poolTokenType == PoolTokenType.Collateral && (<RiskMetrics changeCollateral={val} hideIfNull={true} />) }
      <InputAmount 
        val={val}
        setVal={setVal}
        suffix={data.symbol}
        maxTitle={'Available'}
        max={data.availableBalance}
      />
      <div className="transaction-recap">
        <TransactionSize amount={val} />
        <SupplyAPR amount={val} />
      </div>
      <Row className="interaction-row">
        <Col xs={6}>
          <InteractionButton name="Approve" onCall={onApprove} state={approvalState} />
        </Col>
        <Col xs={6}>
          <InteractionButton name={poolTokenType == PoolTokenType.Collateral ? "Deposit" : "Supply"} onCall={onDeposit} state={depositState} />
        </Col>
      </Row>
    </></InteractionModalContainer>
  );
}